import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Actions, getUserAuditTrail } from "../../../actions/loan.action";
import Alert from "../../../components/alert";
import Button from "../../../components/button";
import { MONTH_SHORT_NAME_LIST } from "../../../constants";
import { AuditTrailResponse } from "../../../dto";
import dispatch from "../../../middleware";
import { LoanType } from "../../../models";
import { ReduxState } from "../../../reducers";
import { isLoadingActive } from "../../../utils";
import "../userDetailsStyles.css";

interface UserAuditTrailProps {
  userId: string;
}

const LOAN_TYPE_LABELS: Record<LoanType, string> = {
  TERM_LOAN: "Term Loan",
  RETAIL_OD: "Earned Wage Access",
  COMMERCIAL_OD: "Commercial OD",
  WORKING_CAPITAL_TERM_LOAN: "Working Capital Term Loan",
};

function loanTypeLabel(loanType: LoanType): string {
  return LOAN_TYPE_LABELS[loanType] ?? loanType;
}

function formatAuditDate(date: string | undefined): string {
  if (!date) return "-";
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const parsed = isDateOnly ? new Date(`${date}T00:00:00`) : new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";
  return `${parsed.getDate().toString().padStart(2, "0")} ${
    MONTH_SHORT_NAME_LIST[parsed.getMonth()]
  } ${parsed.getFullYear()}`;
}

function formatAmount(amount: number): string {
  return amount.toLocaleString("en-IN");
}

export default function UserAuditTrail({ userId }: UserAuditTrailProps) {
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue,
  );
  const auditTrail: AuditTrailResponse | null = useSelector(
    (state: ReduxState) => state.loan.userAuditTrail,
  );
  const errorMessage = useSelector(
    (state: ReduxState) => state.loan.userAuditTrailError,
  );

  const fetchAuditTrail = useCallback(() => {
    dispatch(storeDispatch, getUserAuditTrail(userId));
  }, [storeDispatch, userId]);

  useEffect(() => {
    fetchAuditTrail();
  }, [fetchAuditTrail]);

  const loading = isLoadingActive(loadingQueue, [
    Actions.REQUEST_GET_USER_AUDIT_TRAIL,
  ]);

  if (loading) {
    return <h4 className="text-center">Loading...</h4>;
  }

  if (errorMessage) {
    return (
      <div className="audit-trail">
        <Alert message={errorMessage} type="danger" canDismiss>
          <Button
            type="button"
            variant="danger-outline"
            onClick={fetchAuditTrail}
          >
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const borrowerEvents = auditTrail?.borrowerVerification ?? [];
  const loans = auditTrail?.loanHistory ?? [];

  return (
    <div className="audit-trail">
      {/* Borrower verification — vertical timeline */}
      <div className="audit-trail-section-header">Audit Trail</div>
      <div className="audit-trail-section-body">
        {borrowerEvents.length === 0 ? (
          <div className="audit-trail-no-loans">No verification events.</div>
        ) : (
          borrowerEvents.map((event, index) => (
            <div
              className="audit-trail-vertical-step"
              key={`${event.label}-${index}`}
            >
              <div className="audit-trail-vertical-marker">
                <div className="audit-trail-dot" />
                {index < borrowerEvents.length - 1 && (
                  <div className="audit-trail-vertical-connector" />
                )}
              </div>
              <div className="audit-trail-step-content">
                <div className="audit-trail-step-label">{event.label}</div>
                <div className="audit-trail-step-date">
                  {formatAuditDate(event.date)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Loan history — horizontal stepper per loan */}
      <div className="audit-trail-section-header">Loan History</div>
      <div className="audit-trail-loan-section-body">
        {loans.length === 0 ? (
          <div className="audit-trail-no-loans">No loan taken yet.</div>
        ) : (
          loans.map((loan) => (
            <div className="audit-trail-loan-card mb-4" key={loan.loanId}>
              <div className="audit-trail-loan-header">
                <div className="audit-trail-loan-header-left">
                  <span className="audit-trail-loan-type-badge">
                    {loanTypeLabel(loan.loanType)}
                  </span>
                  <span className="audit-trail-loan-id">
                    Loan ID: {loan.loanId}
                  </span>
                </div>
              </div>
              <div className="audit-trail-horizontal-stepper">
                {loan.timeline.map((event, index) => (
                  <div
                    className="audit-trail-horizontal-step"
                    key={`${event.label}-${index}`}
                  >
                    <div className="audit-trail-horizontal-step-content">
                      <div className="audit-trail-horizontal-dot" />
                      <div className="audit-trail-step-label">
                        {event.label}
                      </div>
                      <div className="audit-trail-step-date">
                        {formatAuditDate(event.date)}
                      </div>
                    </div>
                    {index < loan.timeline.length - 1 && (
                      <div className="audit-trail-horizontal-connector" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
